import {PropsWithChildren, memo, useCallback, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {Navigate, Route, Routes} from 'react-router-dom';

import {
  ConditionalWrap,
  Footer,
  NavigationContainer,
  ProtectedRoute,
  SomethingWrongPage,
} from '@macmillanplatform/ui-core';
import '@macmillanplatform/ui-core/fontawesome';
import {useIsMounted} from '@macmillanplatform/ui-core/hooks';
import '@macmillanplatform/ui-core/styles';

import {privacyLink} from './config';
import {
  COMPANY_LOGO,
  NAVIGATION_LINKS,
  NAVIGATION_LOGO,
  ROUTES,
} from './constants';

import {useAppDispatch} from './hooks';

import {fetchConfig} from './actions';
import {selectConfigLoading, selectSomethingWrong} from './reducer';
import {
  fetchUser,
  selectHasUser,
  selectUserLoading,
} from '@/features/auth/slice';

import SystemAlert from './components/system-alert';
import UserInfoHeader from './components/user-info-header';
import ContactsPage from './pages/contacts';
import HelpPage from './pages/help';
import HomePage from './pages/home';
import LoginPage from './pages/login';
import {AppSetup, Navigation, Notifications} from './shared/components';

import './app.css';

const ProductRoutes = memo(() => {
  const hasUser = useSelector(selectHasUser);

  const defaultRoute = hasUser ? ROUTES.HOME_PAGE : ROUTES.LOGIN_PAGE;

  const getProtectedElement = (hasRedirect: boolean) => (
    <ProtectedRoute path={defaultRoute} hasRedirect={hasRedirect} />
  );

  return (
    <Routes>
      <Route element={getProtectedElement(!hasUser)}>
        <Route path={ROUTES.HOME_PAGE} element={<HomePage />} />
        <Route path={ROUTES.CONTACTS_PAGE} element={<ContactsPage />} />
        <Route path={ROUTES.HELP_PAGE} element={<HelpPage />} />
      </Route>
      <Route element={getProtectedElement(hasUser)}>
        <Route path={ROUTES.LOGIN_PAGE} element={<LoginPage />} />
      </Route>
      <Route path="*" element={<Navigate to={defaultRoute} replace />} />
    </Routes>
  );
});

interface AppLayoutProps extends PropsWithChildren {
  isSomethingWrong: boolean;
}

const AppLayout = memo((props: PropsWithChildren<AppLayoutProps>) => {
  const {isSomethingWrong, children} = props;

  return (
    <Notifications.Provider hasNotifications={true}>
      <NavigationContainer sticky>
        <SystemAlert />
        <Navigation
          className="Product__header"
          logo={NAVIGATION_LOGO}
          menuItems={NAVIGATION_LINKS}
        >
          <UserInfoHeader />
        </Navigation>
      </NavigationContainer>
      {isSomethingWrong ? <SomethingWrongPage /> : children}
      <Footer
        logo={COMPANY_LOGO}
        supportInfo={
          <a
            className="Product__footer-link"
            href={privacyLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Notice
          </a>
        }
      />
    </Notifications.Provider>
  );
});

const App = () => {
  const dispatch = useAppDispatch();
  const isConfigLoading = useSelector(selectConfigLoading);
  const isUserLoading = useSelector(selectUserLoading);
  const hasUser = useSelector(selectHasUser);
  const isSomethingWrong = useSelector(selectSomethingWrong);
  const isMounted = useIsMounted();

  useEffect(() => {
    dispatch(fetchConfig());
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => {
        dispatch(fetchUser());
      },
      60 * 60 * 1000
    );

    return () => clearInterval(interval);
  }, []);

  const handleLoadUser = useCallback(() => dispatch(fetchUser()), [dispatch]);

  return (
    <AppSetup
      isLoading={isConfigLoading || isUserLoading || !isMounted}
      onLoadUser={handleLoadUser}
    >
      <ConditionalWrap
        condition={hasUser}
        wrap={children => (
          <AppLayout isSomethingWrong={isSomethingWrong}>{children}</AppLayout>
        )}
      >
        {isSomethingWrong ? <SomethingWrongPage /> : <ProductRoutes />}
      </ConditionalWrap>
    </AppSetup>
  );
};

export default App;
